"use client";

import { c as _c } from "react/compiler-runtime";
import { ChatClientProvider, ChatRoomProvider } from "@ably/chat/react";
import { useAblyChatClient } from "@/components/providers/AblyChatProvider";
import { usePathname, useSearchParams } from "next/navigation";
import { Component, createContext, type ReactNode, useContext } from "react";
interface WorkflowPresenceProviderProps {
  /** The workflow ID to create a unique room for */
  workflowId?: string;
  children: ReactNode;
}

// Context to indicate whether Ably room features are available.
// True when chatClient is connected and ChatRoomProvider is in the tree.
const ChatRoomContext = createContext<boolean>(false);

/**
 * Hook to check if Ably room features are available.
 * Components using Ably room hooks (usePresence, useRoom, etc.) should check this before rendering.
 */
export function useInChatRoom() {
  return useContext(ChatRoomContext);
}

/**
 * Gate component that only renders children when inside an active Ably chat room.
 * Use this to wrap components that depend on Ably room hooks.
 */
export function ChatRoomGate(t0) {
  const {
    children
  } = t0;
  const inChatRoom = useContext(ChatRoomContext);
  if (!inChatRoom) {
    return null;
  }
  return children;
}

/**
 * Error boundary that catches errors from the single ChatRoomProvider
 * during concurrent mode transitions. Retries up to 3 times before
 * letting children render without Ably features.
 */
class PresenceErrorBoundary extends Component<{
  children: ReactNode;
}, {
  hasError: boolean;
  retries: number;
}> {
  constructor(props: {
    children: ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      retries: 0
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch() {
    // Retry after a tick — the provider tree may have stabilized.
    // Cap retries to avoid infinite loops from persistent errors.
    if (this.state.retries < 3) {
      this.setState(prev => ({
        hasError: false,
        retries: prev.retries + 1
      }));
    }
  }
  render() {
    // Always render children. On persistent errors (>3 retries),
    // stop retrying and let children render without Ably features.
    return this.props.children;
  }
}

/**
 * Inner component that sets up a single ChatRoomProvider for the workflow.
 * A single ChatRoomProvider = one attach lifecycle = stable room.
 * ChatClientProvider is created here at point of use (rather than in the
 * top-level AblyChatProvider) to keep the global provider tree stable and
 * prevent full-page remounts when the Ably connection establishes.
 */
function WorkflowPresenceInner(t0) {
  const $ = _c(8);
  const {
    workflowId,
    children
  } = t0;
  const chatClient = useAblyChatClient();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sortedParams = Array.from(searchParams.entries()).sort(_temp).map(_temp2).join("&");
  const pathWithQuery = sortedParams ? `${pathname}?${sortedParams}` : pathname;
  const sanitizedPath = pathWithQuery.replace(/[/*:\[\],]/g, "-");
  const roomId = workflowId ? `workflow:${workflowId}` : `workflow:path:${sanitizedPath}`;
  if (!chatClient) {
    let t1;
    if ($[0] !== children) {
      t1 = <ChatRoomContext.Provider value={false}>{children}</ChatRoomContext.Provider>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children || $[3] !== roomId) {
    t1 = <ChatRoomProvider name={roomId}>{children}</ChatRoomProvider>;
    $[2] = children;
    $[3] = roomId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== chatClient || $[6] !== t1) {
    t2 = <ChatRoomContext.Provider value={true}><ChatClientProvider client={chatClient}>{t1}</ChatClientProvider></ChatRoomContext.Provider>;
    $[5] = chatClient;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Provider component that sets up real-time presence for a workflow.
 * Wraps children in a single ChatRoomProvider so all leaf components
 * (LiveCursors, LiveAvatarStack, etc.) share one attach lifecycle.
 */
function _temp2(t0) {
  const [k, v] = t0;
  return `${k}=${v}`;
}
function _temp(t0, t1) {
  const [a] = t0;
  const [b] = t1;
  return a.localeCompare(b);
}
export default function WorkflowPresenceProvider(t0) {
  const $ = _c(3);
  const {
    workflowId,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== workflowId) {
    t1 = <PresenceErrorBoundary><WorkflowPresenceInner workflowId={workflowId}>{children}</WorkflowPresenceInner></PresenceErrorBoundary>;
    $[0] = children;
    $[1] = workflowId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}